import { apiFetch, ApiError } from './client'
import type { ActionRequest, ActionResponse } from './types'

const MAX_RETRIES = 3
const BACKOFF_MS = [1000, 3000, 9000]

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Submit an action with retry on 5xx/network errors (AR6).
 * action_id must already be set (generated via crypto.randomUUID()).
 * Successful replays use the normal 2xx response contract.
 */
export async function submitAction(
  request: ActionRequest,
  options: { headers?: Record<string, string> } = {},
): Promise<ActionResponse> {
  let lastError: unknown

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await apiFetch<ActionResponse>('api/v1/actions', {
        method: 'POST',
        body: request,
        headers: options.headers,
      })
    } catch (error) {
      lastError = error
      if (error instanceof ApiError) {
        // Successful command replays are 2xx. A conflict (including an earlier
        // failed command or changed request identity) must never be cast to success.
        if (error.status >= 400 && error.status < 500) throw error
      }
      // Network error or 5xx = retry
      if (attempt < MAX_RETRIES) {
        await sleep(BACKOFF_MS[attempt])
      }
    }
  }

  throw lastError ?? new Error('Max retries exceeded')
}
